import { AnimatePresence, motion } from 'motion/react';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { isServer } from '@utils/helper';
import {
  CLOSE_OVERLAY_DELAY_MS,
  DEFAULT_MODE,
  DEFAULT_OFFSET,
  DEFAULT_OVERLAY_CLASSNAME,
  DEFAULT_PANEL_CLASSNAME,
  DEFAULT_POSITION,
  DEFAULT_STAGGER_INDEX,
  normalizePosition,
  OVERLAY_Z_INDEX_CLASS,
  PANEL_MOTION_TRANSITION,
  triggerStaggerVariants,
  VIEWPORT_EDGE_PADDING,
} from './constants';
import { MotionTooltipProps } from './types';
import { NormalizedMotionTooltipPosition } from './types';

export type { MotionTooltipPosition } from './types';

type TooltipCoords = { top: number; left: number };

const getTriggerCoords = (
  rect: DOMRect,
  offset: number,
  normalizedPosition: NormalizedMotionTooltipPosition,
): TooltipCoords => {
  if (normalizedPosition === 'top-center') {
    return {
      top: rect.top - offset,
      left: rect.left + rect.width / 2,
    };
  }
  if (normalizedPosition === 'bottom-center') {
    return {
      top: rect.bottom + offset,
      left: rect.left + rect.width / 2,
    };
  }
  if (normalizedPosition === 'left-center') {
    return {
      top: rect.top + rect.height / 2,
      left: rect.left - offset,
    };
  }
  return {
    top: rect.top + rect.height / 2,
    left: rect.right + offset,
  };
};

const MotionTooltip: React.FC<MotionTooltipProps> = ({
  children,
  content,
  overlayClassName = DEFAULT_OVERLAY_CLASSNAME,
  panelClassName = DEFAULT_PANEL_CLASSNAME,
  offset = DEFAULT_OFFSET,
  position = DEFAULT_POSITION,
  mode = DEFAULT_MODE,
  disabled = false,
  isVisible,
  staggerIndex = DEFAULT_STAGGER_INDEX,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState<TooltipCoords | null>(null);
  const [viewportShift, setViewportShift] = useState({ x: 0, y: 0 });
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const panelMeasureRef = useRef<HTMLDivElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const frameRef = useRef<number | null>(null);
  const normalizedPosition = normalizePosition(position);

  const clearCloseTimer = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const openOverlay = () => {
    if (disabled) return;
    clearCloseTimer();
    setIsOpen(true);
  };

  const closeOverlay = () => {
    if (disabled) return;
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setIsOpen(false);
    }, CLOSE_OVERLAY_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      clearCloseTimer();
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
      }
    };
  }, []);

  useLayoutEffect(() => {
    if (!isOpen || isServer) {
      setCoords(null);
      setViewportShift({ x: 0, y: 0 });
      return;
    }
    const trigger = triggerRef.current;
    if (!trigger) return;
    setViewportShift({ x: 0, y: 0 });
    setCoords(
      getTriggerCoords(
        trigger.getBoundingClientRect(),
        offset,
        normalizedPosition,
      ),
    );
  }, [isOpen, offset, normalizedPosition]);

  useLayoutEffect(() => {
    if (!isOpen || isServer || coords === null) return;
    const panel = panelMeasureRef.current;
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const pad = VIEWPORT_EDGE_PADDING;
    let shiftX = 0;
    let shiftY = 0;
    if (rect.right > window.innerWidth - pad) {
      shiftX = window.innerWidth - pad - rect.right;
    }
    if (rect.left + shiftX < pad) {
      shiftX = pad - rect.left;
    }
    if (rect.top + shiftY < pad) {
      shiftY = pad - rect.top;
    }
    if (rect.bottom + shiftY > window.innerHeight - pad) {
      shiftY = window.innerHeight - pad - rect.bottom;
    }
    setViewportShift(prev =>
      prev.x === shiftX && prev.y === shiftY ? prev : { x: shiftX, y: shiftY },
    );
  }, [isOpen, coords, normalizedPosition, content, mode]);

  useEffect(() => {
    if (!isOpen || isServer) return;
    const updatePosition = () => {
      const trigger = triggerRef.current;
      if (!trigger) return;
      setViewportShift({ x: 0, y: 0 });
      setCoords(
        getTriggerCoords(
          trigger.getBoundingClientRect(),
          offset,
          normalizedPosition,
        ),
      );
    };
    const scheduleUpdatePosition = () => {
      if (frameRef.current !== null) return;
      frameRef.current = requestAnimationFrame(() => {
        frameRef.current = null;
        updatePosition();
      });
    };
    const onEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false);
    };
    window.addEventListener('resize', scheduleUpdatePosition);
    window.addEventListener('scroll', scheduleUpdatePosition, {
      capture: true,
      passive: true,
    });
    document.addEventListener('keydown', onEscape);
    return () => {
      if (frameRef.current !== null) {
        cancelAnimationFrame(frameRef.current);
        frameRef.current = null;
      }
      window.removeEventListener('resize', scheduleUpdatePosition);
      window.removeEventListener('scroll', scheduleUpdatePosition, true);
      document.removeEventListener('keydown', onEscape);
    };
  }, [isOpen, offset, normalizedPosition]);

  return (
    <motion.div
      ref={triggerRef}
      initial={false}
      animate={
        typeof isVisible === 'boolean'
          ? isVisible
            ? 'visible'
            : 'hidden'
          : undefined
      }
      custom={staggerIndex}
      variants={triggerStaggerVariants}
      className={`relative flex ${
        isVisible === false ? 'pointer-events-none' : ''
      }`}
      onMouseEnter={openOverlay}
      onMouseLeave={closeOverlay}
      onFocus={openOverlay}
      onBlur={closeOverlay}
    >
      {children}
      {!disabled &&
        !isServer &&
        createPortal(
          <AnimatePresence>
            {isOpen && coords !== null && (
              <div
                className={`fixed ${OVERLAY_Z_INDEX_CLASS} ${overlayClassName}`}
                style={{
                  top: coords.top + viewportShift.y,
                  left: coords.left + viewportShift.x,
                }}
                onMouseEnter={openOverlay}
                onMouseLeave={closeOverlay}
              >
                <div
                  className={`absolute left-0 top-0 ${
                    normalizedPosition === 'left-center'
                      ? '-translate-x-full -translate-y-1/2'
                      : normalizedPosition === 'right-center'
                      ? '-translate-y-1/2'
                      : normalizedPosition === 'bottom-center'
                      ? '-translate-x-1/2'
                      : '-translate-x-1/2 -translate-y-full'
                  }`}
                >
                  <motion.div
                    ref={panelMeasureRef}
                    initial={
                      normalizedPosition === 'left-center'
                        ? { opacity: 0, x: 6, scale: 0.98 }
                        : normalizedPosition === 'right-center'
                        ? { opacity: 0, x: -6, scale: 0.98 }
                        : normalizedPosition === 'bottom-center'
                        ? { opacity: 0, y: -6, scale: 0.98 }
                        : { opacity: 0, y: 6, scale: 0.98 }
                    }
                    animate={
                      normalizedPosition === 'left-center' ||
                      normalizedPosition === 'right-center'
                        ? { opacity: 1, x: 0, scale: 1 }
                        : { opacity: 1, y: 0, scale: 1 }
                    }
                    exit={
                      normalizedPosition === 'left-center'
                        ? { opacity: 0, x: 6, scale: 0.98 }
                        : normalizedPosition === 'right-center'
                        ? { opacity: 0, x: -6, scale: 0.98 }
                        : normalizedPosition === 'bottom-center'
                        ? { opacity: 0, y: -6, scale: 0.98 }
                        : { opacity: 0, y: 6, scale: 0.98 }
                    }
                    transition={PANEL_MOTION_TRANSITION}
                  >
                    <div
                      className={`rounded-xl w-max ring ring-white/10 bg-black/50 backdrop-blur-sm text-white p-2 shadow-sm ${panelClassName}`}
                    >
                      {mode === 'popover' ? (
                        content
                      ) : (
                        <span className="text-xs font-medium">{content}</span>
                      )}
                    </div>
                  </motion.div>
                </div>
              </div>
            )}
          </AnimatePresence>,
          document.body,
        )}
    </motion.div>
  );
};

export default MotionTooltip;
